import { VoidCallback } from '@deqo/foundation';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { waitForOperation } from '../../../api/endpoints/operations';
import { useResolve } from '../../Container';
import { busyPopup } from '../../popups/BusyPopup';
import { confirmPopupWith } from '../../popups/OptionsPopup';
import { PopupManager } from '../../popups/PopupManager';
import { teamLocationSelectPopup } from '../../workspace/TeamLocationSelectPopup';
import { selectUserRolePopup } from '../SelectUserRolePopup';

export const useOnBulkRemoveUserFromAccount = (userIds: string[], onOpen?: VoidCallback) => {

    const popups = useResolve(PopupManager);
    const api = useResolve(ApiClient);
    const qc = useQueryClient();

    return useCallback(async () => {

        onOpen?.();

        await popups.invoke(busyPopup(
            async () => {
                const { executionId } = await api.exec(v2.admin.users.remove2({ userIds }));
                await waitForOperation(api, executionId);
                await qc.invalidateQueries(['admin.users.list']);
                await qc.invalidateQueries(['admin.users.stats']);
            }
        ));

    }, [userIds, onOpen]);

};

export const useOnBulkSetUserRoleForAccount = (userIds: string[], onOpen?: VoidCallback) => {

    const popups = useResolve(PopupManager);
    const api = useResolve(ApiClient);
    const qc = useQueryClient();

    return useCallback(async () => {

        onOpen?.();

        const { actioned, data } = await popups.invoke(selectUserRolePopup());
        if (!actioned || !data) return;

        const { name: roleName } = data;

        await popups.invoke(busyPopup(
            async () => {
                const { executionId } = await api.exec(v2.admin.users.setRole2({ userIds, roleName }));
                await waitForOperation(api, executionId);
                await qc.invalidateQueries(['admin.users.list']);
            }
        ));

    }, [userIds, onOpen]);
};

export const useOnBulkApproveDirectInvites = (inviteIds: string[], onOpen?: VoidCallback) => {

    const popups = useResolve(PopupManager);
    const api = useResolve(ApiClient);
    const qc = useQueryClient();

    return useCallback(async () => {

        onOpen?.();

        await popups.invoke(busyPopup(
            async () => {
                const { executionId } = await api.exec(v2.admin.directInvites.approve({ inviteIds }));
                await waitForOperation(api, executionId);
                await qc.invalidateQueries(['admin.directInvites.list']);
                await qc.invalidateQueries(['admin.directInvites.count']);
            }
        ));

    }, [inviteIds, onOpen]);
};

export const useOnBulkRejectDirectInvites = (inviteIds: string[], onOpen?: VoidCallback) => {

    const popups = useResolve(PopupManager);
    const api = useResolve(ApiClient);
    const qc = useQueryClient();

    return useCallback(async () => {

        onOpen?.();

        await popups.invoke(busyPopup(
            async () => {
                const { executionId } = await api.exec(v2.admin.directInvites.reject({ inviteIds }));
                await waitForOperation(api, executionId);
                await qc.invalidateQueries(['admin.directInvites.list']);
                await qc.invalidateQueries(['admin.directInvites.count']);
            }
        ));

    }, [inviteIds, onOpen]);
};

export const useOnBulkMoveBoards = (boardIds: string[], onOpen?: VoidCallback) => {

    const popups = useResolve(PopupManager);
    const api = useResolve(ApiClient);
    const qc = useQueryClient();

    return useCallback(async () => {

        onOpen?.();

        const { actioned, data } = await popups.invoke(teamLocationSelectPopup({
            mode: 'admin',
            title: 'Move Boards',
            label: 'Where do you want to move these boards?',
            primary: 'Move Boards',
        }));

        if (!actioned || !data) return;
        const workspaceId = data.workspaceId;
        const folderId = data.folderId || null;

        await popups.invoke(busyPopup(
            async () => {
                const { executionId } = await api.exec(v2.admin.boards.move({ boardIds, toId: folderId || workspaceId }));
                await waitForOperation(api, executionId);
                await qc.invalidateQueries(['admin.boards.list']);
            }
        ));

    }, [boardIds, onOpen]);

};

const archiveConfirmationPopup = () => confirmPopupWith(
    'Are you sure you want to archive these boards?',
    'Once archived, boards will be unavailable to team members.  To undo, you can still move them back into a team later.',
);

export const useOnBulkArchiveBoards = (boardIds: string[], onOpen?: VoidCallback) => {

    const popups = useResolve(PopupManager);
    const api = useResolve(ApiClient);
    const qc = useQueryClient();

    return useCallback(async () => {

        onOpen?.();

        const { actioned, data } = await popups.invoke(archiveConfirmationPopup());
        if (!actioned || !data) return;

        await popups.invoke(busyPopup(
            async () => {
                const { executionId } = await api.exec(v2.admin.boards.archive({ boardIds }));
                await waitForOperation(api, executionId);
                await qc.invalidateQueries(['admin.boards.list']);
            }
        ));

    }, [boardIds, onOpen]);

};

const deleteConfirmationPopup = () => confirmPopupWith(
    'Are you sure you want to delete these boards?',
    'This is not reversible.  All board data will be removed.',
);

export const useOnBulkDeleteBoards = (boardIds: string[], onOpen?: VoidCallback) => {

    const popups = useResolve(PopupManager);
    const api = useResolve(ApiClient);
    const qc = useQueryClient();

    return useCallback(async () => {

        onOpen?.();

        const { actioned, data } = await popups.invoke(deleteConfirmationPopup());
        if (!actioned || !data) return;

        await popups.invoke(busyPopup(
            async () => {
                const { executionId } = await api.exec(v2.admin.boards.delete({ boardIds }));
                await waitForOperation(api, executionId);
                await qc.invalidateQueries(['admin.boards.list']);
            }
        ));

    }, [boardIds, onOpen]);

};
